import { createFileRoute, Outlet, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Building2, Users, History } from "lucide-react";
import {
  FeedbackState,
  NavigationMenu,
  PageContent,
  PageHeader,
  PageShell,
  SettingsLayout,
  type NavigationItem,
} from "@h2j-data-team/ui";

import { renderLink } from "@/components/router-link";

export const Route = createFileRoute("/_authenticated/settings")({
  staticData: { pageTitle: "breadcrumbs.settings" },
  component: SettingsLayoutPage,
  // An unknown settings section: a compact state inside the settings layout (the
  // full-page ErrorPage overflowed the section area).
  notFoundComponent: SettingsNotFound,
});

function SettingsNotFound() {
  const { t } = useTranslation("common");
  return (
    <FeedbackState
      state="empty"
      title={t("error.pageNotFound")}
      description={t("error.pageNotFoundMessage")}
    />
  );
}

const SECTIONS = [
  { id: "organization", icon: <Building2 /> },
  { id: "members", icon: <Users /> },
  { id: "audit", icon: <History /> },
] as const;

function SettingsLayoutPage() {
  const { t } = useTranslation("organizations");
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const activeId = pathname.includes("/members")
    ? "members"
    : pathname.includes("/audit")
      ? "audit"
      : "organization";

  const items: NavigationItem[] = SECTIONS.map(({ id, icon }) => ({
    id,
    icon,
    href: `/settings/${id}`,
    active: activeId === id,
    label: t(`settings.layout.nav.${id}.label`),
    description: t(`settings.layout.nav.${id}.description`),
  }));

  return (
    <PageShell
      header={
        <PageHeader
          title={t("settings.layout.pageTitle")}
          description={t("settings.layout.subtitle")}
        />
      }
    >
      <PageContent>
        <SettingsLayout
          navigation={
            <NavigationMenu
              label={t("settings.layout.ariaLabel")}
              items={items}
              renderLink={renderLink}
            />
          }
        >
          <Outlet />
        </SettingsLayout>
      </PageContent>
    </PageShell>
  );
}
